import type { AreaComponent } from '@repo/api-contracts';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { type ReactElement, useEffect, useId, useRef } from 'react';
import { componentSizeLabel } from '@/shared/lib';
import { scrollIntoViewAfterRelease } from '../model/scroll-into-view-after-release';
import { AreaInspector } from './area-inspector';

interface AreaListItemProps {
  readonly component: AreaComponent;
  /** 이 면 배치 여부 태그 — '이 면' · '다른 면만' · '배치 안 됨'. */
  readonly tag: string;
  /** 펼쳐졌는가(= 고른 영역인가). */
  readonly open: boolean;
  readonly disabled: boolean;
  /** 머리줄을 눌렀을 때. 접힌 줄이면 고르고, 펼친 줄이면 선택을 푼다(호출자가 정한다). */
  readonly onToggle: () => void;
}

/**
 * 인쇄 영역 접이 줄 하나 — 머리줄(이름·크기·태그·위아래 화살표)과, 펼치면 그 영역의 인스펙터.
 * 펼쳐지면(사진에서 골랐을 때도) 줄이 보이도록 스크롤한다 — 사진 위에서 누른 채면 포인터를 놓은 뒤에 스크롤한다.
 */
export const AreaListItem = ({
  component,
  tag,
  open,
  disabled,
  onToggle,
}: AreaListItemProps): ReactElement => {
  const id = useId();
  const nameId = `${id}-name`;
  const detailId = `${id}-detail`;
  const panelId = `${id}-panel`;
  const itemRef = useRef<HTMLLIElement>(null);
  useEffect(() => {
    const item = itemRef.current;
    if (!open || item === null) return;
    // 사진 위에서 누른 채 끄는 중이면 놓을 때까지 미룬다(끄는 동안 사진이 움직이지 않게).
    return scrollIntoViewAfterRelease(item);
  }, [open]);
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <li ref={itemRef} className="rounded border">
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-labelledby={nameId}
        aria-describedby={detailId}
        disabled={disabled}
        onClick={onToggle}
      >
        <span className="min-w-0 flex-1">
          <span id={nameId} className="block truncate font-medium">
            {component.name}
          </span>
          <span id={detailId} className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground">{componentSizeLabel(component)}</span>
            <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
              {tag}
            </span>
          </span>
        </span>
        <Chevron aria-hidden="true" className="size-4 shrink-0" />
      </button>
      {open ? (
        <section id={panelId} aria-labelledby={nameId} className="border-t p-3">
          <AreaInspector component={component} />
        </section>
      ) : null}
    </li>
  );
};
